import {GQLQueryProvider} from "../gqlModel/GQLQueryProvider";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {createAudit, updateAudit, deleteAudit} from "../../graphql/audit/mutations";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import {AuditTemplate, CreateAuditTemplateInput, UpdateAuditTemplateInput} from "./types";
import type {AuditTemplateModelType} from "./AuditTemplateModel";

const ENDPOINT = "pgServer";

interface Deps {
    dataClientService: IDataClientService;
    authService: IAuthService;
}

export class AuditTemplateGqlProvider extends GQLQueryProvider {
    private readonly dataClientService: IDataClientService;
    private readonly authService: IAuthService;
    private readonly wsId: string;

    constructor(deps: Deps, wsId: string) {
        super();
        this.dataClientService = deps.dataClientService;
        this.authService = deps.authService;
        this.wsId = wsId;
    }

    /** Called by GqlModelBase.save() — reads fields from the MST model instance. */
    save = async (model: AuditTemplateModelType): Promise<AuditTemplate> => {
        if (model.isNew) {
            return this.create({
                name: model.name,
                description: model.description,
                requiredPassPercentage: model.requiredPassPercentage,
                publicationState: model.publicationState as 'draft' | 'published',
            });
        }
        return this.update({
            id: model.id,
            name: model.name,
            description: model.description,
            requiredPassPercentage: model.requiredPassPercentage,
            publicationState: model.publicationState as 'draft' | 'published',
        });
    };

    create(input: Omit<CreateAuditTemplateInput, 'workspaceId'>): Promise<AuditTemplate> {
        return queryGqlApi(
            createAudit, {input: {...input, workspaceId: this.wsId}},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<AuditTemplate>;
    }

    update(input: UpdateAuditTemplateInput): Promise<AuditTemplate> {
        return queryGqlApi(
            updateAudit, {input},
            this.dataClientService, this.authService, ENDPOINT,
        ) as Promise<AuditTemplate>;
    }

    async delete(id: string): Promise<void> {
        await queryGqlApi(
            deleteAudit, {id},
            this.dataClientService, this.authService, ENDPOINT,
        );
    }
}
